import { useState, useMemo } from 'react';

// Professional recipe card — Tally-sourced, read-only training material.
// Photo hero, Hot/Iced toggle, size chips, an ingredient table with units,
// and numbered instructions. No cost (this is a barista training card).
//
// card shape (from GET /api/recipe-cards/:itemId):
//   { name, category, photo, steps:{hot:[],iced:[]},
//     sizes:[{size, temp, ingredients:[{name, qty, unit}], allergens:{contains,complete}|null}],
//     station, equipment, prep_seconds, has_any_recipe }

const FOREST = '#1f4d3a';

// "fl_oz" → "fl oz", "each" → "ct". Keep it readable on the line.
function prettyUnit(u) {
  if (!u) return '';
  const map = { fl_oz: 'fl oz', each: 'ct', g: 'g', ml: 'ml', oz: 'oz' };
  return map[u] || String(u).replace(/_/g, ' ');
}
function prettyQty(q) {
  if (q == null) return '';
  const n = Number(q);
  if (!Number.isFinite(n)) return String(q);
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '');
}
// FDA 9, labelled the way Tally prints them on labels.
const ALLERGEN_LABEL = {
  milk: 'Milk', eggs: 'Eggs', fish: 'Fish', crustacean_shellfish: 'Shellfish',
  tree_nuts: 'Tree nuts', peanuts: 'Peanuts', wheat: 'Wheat', soybeans: 'Soy', sesame: 'Sesame',
};

// Tally sends { contains, complete } per size. An empty list only means
// "none" when complete — otherwise an ingredient is still unchecked in Tally,
// and saying "no allergens" to a guest would be a guess.
export function allergenLine(a) {
  if (!a) return { tone: 'unknown', text: 'Allergens not set up in Tally yet' };
  const names = a.contains.map((k) => ALLERGEN_LABEL[k] || k);
  if (names.length === 0) {
    return a.complete
      ? { tone: 'none', text: 'No major allergens' }
      : { tone: 'unknown', text: 'Allergens not yet verified in Tally' };
  }
  return {
    tone: 'contains',
    text: `Contains: ${names.join(', ')}`,
    note: a.complete ? null : 'Not every ingredient is verified yet — may contain more.',
  };
}

const ALLERGEN_TONE = {
  contains: { bg: '#fff4e5', border: '#f5c27a', fg: '#7a4a0c' },
  none:     { bg: '#eef6f1', border: '#b8d8c4', fg: FOREST },
  unknown:  { bg: '#f3f4f6', border: '#e5e7eb', fg: '#6b7280' },
};

function prepLabel(secs) {
  if (!secs) return null;
  const m = Math.round(secs / 60);
  return m >= 1 ? `${m} min` : `${secs}s`;
}

export function RecipeCard({ card, onClose }) {
  const temps = useMemo(() => {
    const set = new Set((card?.sizes || []).map((s) => s.temp));
    // Prefer showing a temp that actually has steps or ingredients first.
    return ['hot', 'iced'].filter((t) => set.has(t));
  }, [card]);

  const [temp, setTemp] = useState(temps[0] || 'hot');
  const sizesForTemp = (card?.sizes || []).filter((s) => s.temp === temp);
  const [sizeIdx, setSizeIdx] = useState(0);
  const size = sizesForTemp[Math.min(sizeIdx, Math.max(0, sizesForTemp.length - 1))] || null;
  const steps = (card?.steps?.[temp]) || [];

  const label = { hot: 'Hot', iced: 'Iced' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
                   display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                   padding: '24px 12px', overflowY: 'auto' }}
         onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           style={{ background: '#fff', borderRadius: 16, maxWidth: 720, width: '100%',
                     boxShadow: '0 12px 40px rgba(0,0,0,0.25)', overflow: 'hidden', fontFamily: 'inherit' }}>
        {/* Hero */}
        <div style={{ position: 'relative' }}>
          {card.photo ? (
            <img src={card.photo} alt={card.name}
                 style={{ width: '100%', height: 260, objectFit: 'contain', objectPosition: 'center',
                           display: 'block', background: '#f4f4f3' }} loading="lazy" />
          ) : (
            <div style={{ width: '100%', height: 120, background: `linear-gradient(135deg, ${FOREST}, #2e6b4f)` }} />
          )}
          <button onClick={onClose} aria-label="Close"
                  style={{ position: 'absolute', top: 12, right: 12, width: 34, height: 34, borderRadius: '50%',
                            border: 'none', background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 18,
                            cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: '20px 24px 28px' }}>
          {card.category && (
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: FOREST }}>
              {card.category}
            </div>
          )}
          <h2 style={{ fontSize: 26, fontWeight: 800, color: '#1a1a1a', margin: '2px 0 6px' }}>{card.name}</h2>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12, color: '#6b7280' }}>
            {card.station && <span>🧑‍🍳 {card.station}</span>}
            {prepLabel(card.prep_seconds) && <span>⏱ {prepLabel(card.prep_seconds)}</span>}
            {card.equipment && <span>⚙ {card.equipment}</span>}
          </div>

          {/* Temp toggle */}
          {temps.length > 0 && (
            <div style={{ display: 'inline-flex', marginTop: 16, background: '#f3f4f6', borderRadius: 10, padding: 3 }}>
              {temps.map((t) => (
                <button key={t} onClick={() => { setTemp(t); setSizeIdx(0); }}
                        style={{ border: 'none', cursor: 'pointer', padding: '7px 18px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                                  background: temp === t ? '#fff' : 'transparent', color: temp === t ? FOREST : '#6b7280',
                                  boxShadow: temp === t ? '0 1px 3px rgba(0,0,0,0.12)' : 'none' }}>
                  {label[t]}
                </button>
              ))}
            </div>
          )}

          {/* Allergens for the size being shown — sizes can differ. */}
          {(() => {
            const line = allergenLine(size && size.allergens);
            const t = ALLERGEN_TONE[line.tone];
            return (
              <div style={{ marginTop: 16, background: t.bg, border: `1px solid ${t.border}`, color: t.fg,
                             borderRadius: 10, padding: '9px 12px', fontSize: 13 }}>
                <div style={{ fontWeight: 700 }}>
                  {line.tone === 'contains' ? '⚠ ' : ''}{line.text}{size && sizesForTemp.length > 1 ? <span style={{ fontWeight: 500 }}> · {size.size}</span> : null}
                </div>
                {line.note && <div style={{ fontSize: 12, marginTop: 2 }}>{line.note}</div>}
                {line.tone !== 'unknown' && (
                  <div style={{ fontSize: 11.5, marginTop: 2, opacity: 0.85 }}>
                    Standard recipe — milk swaps and add-ins change this.
                  </div>
                )}
              </div>
            );
          })()}

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.2fr)', gap: 28, marginTop: 20 }}>
            {/* Ingredients */}
            <div>
              <div style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#374151', marginBottom: 8 }}>
                Ingredients
              </div>
              {sizesForTemp.length > 1 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                  {sizesForTemp.map((s, i) => (
                    <button key={i} onClick={() => setSizeIdx(i)}
                            style={{ border: `1px solid ${i === sizeIdx ? FOREST : '#d1d5db'}`, cursor: 'pointer',
                                      background: i === sizeIdx ? FOREST : '#fff', color: i === sizeIdx ? '#fff' : '#374151',
                                      borderRadius: 999, padding: '3px 12px', fontSize: 12, fontWeight: 600 }}>
                      {s.size}
                    </button>
                  ))}
                </div>
              )}
              {size && size.ingredients.length > 0 ? (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <tbody>
                    {size.ingredients.map((g, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f1f2f4' }}>
                        <td style={{ padding: '7px 0', color: '#1a1a1a' }}>{g.name}</td>
                        <td style={{ padding: '7px 0', textAlign: 'right', color: '#374151', fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {prettyQty(g.qty)} <span style={{ color: '#9ca3af', fontWeight: 500 }}>{prettyUnit(g.unit)}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>Ingredients not set up in Tally yet</div>
              )}
            </div>

            {/* Instructions */}
            <div>
              <div style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#374151', marginBottom: 8 }}>
                {label[temp] || ''} instructions
              </div>
              {steps.length > 0 ? (
                <ol style={{ margin: 0, paddingLeft: 0, listStyle: 'none', counterReset: 'step' }}>
                  {steps.map((s, i) => (
                    <li key={i} style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'flex-start' }}>
                      <span style={{ flexShrink: 0, width: 24, height: 24, borderRadius: '50%', background: FOREST, color: '#fff',
                                      fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {i + 1}
                      </span>
                      <span style={{ fontSize: 14, lineHeight: 1.5, color: '#1a1a1a', paddingTop: 2 }}>{s}</span>
                    </li>
                  ))}
                </ol>
              ) : (
                <div style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic',
                               background: '#f9fafb', border: '1px dashed #e5e7eb', borderRadius: 10, padding: '14px 16px' }}>
                  {label[temp]} instructions haven't been written yet — they'll appear here once entered in Tally.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
